import { useBooleanState } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { ArrowRoundLeftIcon, BlockIcon } from '@calendly/ui/components/icons'
import { useContext } from 'react'

import NoShowModal from 'components/scheduled_events/modals/no_show'
import { useTrack } from 'hooks/analytics_events'
import { isCalendlyMeeting } from 'shared/utils'

import { MeetingDetailDependenciesContext } from '../../../meeting_detail_context'

const MarkAsNoShowButton = ({ invitee, event, onSubmit, sendAnalytics }) => {
  const dependencies = useContext(MeetingDetailDependenciesContext)
  if (!dependencies?.useMarkInviteeAsNoShowMutation) {
    throw new Error('MarkAsNoShowButton must be wrapped in a MeetingDetailDependenciesProvider with useMarkInviteeAsNoShowMutation')
  }

  const [markAsNoShow] = dependencies.useMarkInviteeAsNoShowMutation()
  const [isModalOpen, openModal, closeModal] = useBooleanState(false)
  const tooltip = I18n.t('publisher.meetings.invitees.no_show_description_no_show_workflow')

  const handleClick = (e) => {
    e.stopPropagation()
    openModal()
  }
  const handleSubmit = () => {
    markAsNoShow({ id: invitee.id, eventId: event.id, eventUuid: event.uuid })
    sendAnalytics()
    onSubmit()
  }

  return (
    <>
      <Button
        decoration="secondary-outline"
        size="small"
        onClick={handleClick}
        tooltip={tooltip}
      >
        <BlockIcon spacingRight={4} />
        {I18n.t('publisher.meetings.invitees.mark_no_show')}
      </Button>
      {isModalOpen && (
        <NoShowModal
          inviteeName={invitee.name}
          onConfirm={handleSubmit}
          onClose={closeModal}
        />
      )}
    </>
  )
}

const UndoNoShowButton = ({ invitee, event, sendAnalytics }) => {
  const dependencies = useContext(MeetingDetailDependenciesContext)
  if (!dependencies?.useUndoNoShowForInviteeMutation) {
    throw new Error('UndoNoShowButton must be wrapped in a MeetingDetailDependenciesProvider with useUndoNoShowForInviteeMutation')
  }

  const [undoNoShow] = dependencies.useUndoNoShowForInviteeMutation()

  const handleClick = (e) => {
    e.stopPropagation()
    undoNoShow({ id: invitee.id, eventId: event.id, eventUuid: event.uuid })
    sendAnalytics()
  }

  return (
    <Button decoration="secondary-outline" onClick={handleClick} size="small">
      <ArrowRoundLeftIcon spacingRight={4} />
      {I18n.t('publisher.meetings.invitees.undo_no_show')}
    </Button>
  )
}

export const NoShowButton = ({ onMarkAsNoShow, ...props }) => {
  const { event } = props
  const track = useTrack()
  const sendAnalyticsForNoShow = () => {
    track({
      name: 'Meeting Interaction',
      properties: {
        action: 'mark_no_show',
        location: 'meetings_page_right_rail',
        event_uuid: event.uuid,
        mi_event_uuid: event.meetingIntelligence?.uuid,
        is_calendly_meeting: isCalendlyMeeting(event),
      }
    })
  }
  const sendAnalyticsForNoShowUndo = () => {
    track({
      name: 'Meeting Interaction',
      properties: {
        action: 'mark_no_show_undo',
        location: 'meetings_page_right_rail',
        event_uuid: event.uuid,
        mi_event_uuid: event.meetingIntelligence?.uuid,
        is_calendly_meeting: isCalendlyMeeting(event),
      }
    })
  }

  return (
    props.invitee.noShow ? (
      <UndoNoShowButton
        sendAnalytics={sendAnalyticsForNoShowUndo}
        {...props}
      />
    ) : (
      <MarkAsNoShowButton
        onSubmit={onMarkAsNoShow}
        sendAnalytics={sendAnalyticsForNoShow}
        {...props}
      />
    )
  )
}
